import { useMemo, useState } from 'react';
import { format } from 'date-fns';

import AppDateField from '@/components/AppDateField';
import { supabase } from '@/integrations/supabase/client';
import { useApp } from '@/contexts/AppContext';
import { toast } from 'sonner';
import {
  AppBottomSheet,
  AppBottomSheetHeader,
  AppButton,
  AppButtonRow,
  AppCardBody,
  AppFieldGroup,
  AppInput,
  AppNativeSelect,
  AppText,
  AppTextArea,
} from '../../../ui-foundation';
import {
  buildCompletedReminderTimestamp,
  buildPetCareLogOptions,
  mapPetCareLogTypeToReminderType,
  type PetCareLogType,
} from '../../../ui-foundation/features/pets';
import type { HealthSubjectOption } from './health-shared';

type Props = {
  onClose: () => void;
  onSaved: () => void;
  initialSubjectValue: string;
  subjectOptions: HealthSubjectOption[];
};

function todayDate() {
  return format(new Date(), 'yyyy-MM-dd');
}

export default function PetCareLogForm({ onClose, onSaved, initialSubjectValue, subjectOptions }: Props) {
  const { user } = useApp();
  const [subjectValue, setSubjectValue] = useState(initialSubjectValue);
  const selectedSubject = subjectOptions.find((option) => option.value === subjectValue) || subjectOptions[0];
  const petName = selectedSubject?.subjectName || 'this pet';
  const logOptions = useMemo(() => buildPetCareLogOptions(petName), [petName]);
  const [careType, setCareType] = useState<PetCareLogType>(logOptions[0]?.key || 'vet-visit');
  const selectedLogOption = logOptions.find((option) => option.key === careType) || logOptions[0];
  const [title, setTitle] = useState(selectedLogOption?.title || '');
  const [completedDate, setCompletedDate] = useState<Date | undefined>(new Date());
  const [nextDueDate, setNextDueDate] = useState<Date | undefined>(undefined);
  const [notes, setNotes] = useState(selectedLogOption?.notes || '');
  const [saving, setSaving] = useState(false);

  const handleCareTypeChange = (nextValue: string) => {
    const nextType = (nextValue as PetCareLogType) || 'vet-visit';
    const nextOption = logOptions.find((option) => option.key === nextType) || logOptions[0];
    setCareType(nextType);
    setTitle(nextOption?.title || '');
    setNotes(nextOption?.notes || '');
  };

  const handleSubjectChange = (nextValue: string) => {
    setSubjectValue(nextValue);
    const nextSubject = subjectOptions.find((option) => option.value === nextValue) || subjectOptions[0];
    const nextOptions = buildPetCareLogOptions(nextSubject?.subjectName || 'this pet');
    const nextOption = nextOptions.find((option) => option.key === careType) || nextOptions[0];
    setTitle(nextOption?.title || '');
    setNotes(nextOption?.notes || '');
  };

  const canSave = Boolean(
    user &&
      selectedSubject &&
      selectedSubject.subjectType === 'pet' &&
      selectedSubject.subjectId &&
      title.trim().length &&
      completedDate,
  );

  const handleSave = async () => {
    if (!user || !selectedSubject || selectedSubject.subjectType !== 'pet' || !selectedSubject.subjectId || !completedDate) {
      return;
    }

    setSaving(true);
    const completedDateIso = format(completedDate, 'yyyy-MM-dd');
    const nextDueIso = nextDueDate ? format(nextDueDate, 'yyyy-MM-dd') : null;
    const reminderType = mapPetCareLogTypeToReminderType(careType);
    const basePayload = {
      user_id: user.id,
      type: reminderType,
      title: title.trim(),
      notes: notes.trim() || null,
      subject_type: 'pet',
      subject_id: selectedSubject.subjectId,
      subject_name: selectedSubject.subjectName,
      recurrence_type: 'one-off',
      recurrence_interval: null,
      recurrence_unit: null,
      recurrence_day_of_week: null,
      recurrence_week_of_month: null,
      recurrence_month_day: null,
      frequency_months: null,
      reminder_days_before: null,
    } as const;

    const completedPayload = {
      ...basePayload,
      due_date: completedDateIso,
      due_time: null,
      completed_at: buildCompletedReminderTimestamp(completedDateIso),
    };

    const inserts = [completedPayload];

    if (nextDueIso) {
      inserts.push({
        ...basePayload,
        due_date: nextDueIso,
        due_time: null,
        completed_at: null,
      });
    }

    const { error } = await supabase.from('health_reminders').insert(inserts as any);

    if (error) {
      console.error(error);
      toast.error('Could not save that pet care log right now.');
      setSaving(false);
      return;
    }

    setSaving(false);
    onSaved();
  };

  return (
    <AppBottomSheet onClose={onClose}>
      <AppBottomSheetHeader
        title="Log pet care"
        subtitle="Keep vet visits, boosters, grooming, and cleaning in the same Health history instead of trying to remember it later."
      />

      <AppCardBody className="flex-1 overflow-y-auto px-5 py-5" gap="$lg">
        <AppFieldGroup label="Pet">
          <AppNativeSelect value={subjectValue} onChange={(event) => handleSubjectChange(event.target.value)}>
            {subjectOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </AppNativeSelect>
        </AppFieldGroup>

        <AppFieldGroup label="Care type">
          <AppNativeSelect value={careType} onChange={(event) => handleCareTypeChange(event.target.value)}>
            {logOptions.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </AppNativeSelect>
        </AppFieldGroup>

        {selectedLogOption?.notes ? (
          <AppText fontSize="$xs" color="$colorMuted">
            {selectedLogOption.notes}
          </AppText>
        ) : null}

        <AppFieldGroup label="Title">
          <AppInput
            tag="input"
            value={title}
            onChange={(event) => setTitle((event.target as HTMLInputElement).value)}
            placeholder="What happened?"
          />
        </AppFieldGroup>

        <AppDateField
          label="Done on"
          value={completedDate ? format(completedDate, 'yyyy-MM-dd') : todayDate()}
          onChange={(nextValue) => setCompletedDate(nextValue ? new Date(nextValue) : undefined)}
        />

        <AppDateField
          label="Next due date (optional)"
          value={nextDueDate ? format(nextDueDate, 'yyyy-MM-dd') : ''}
          onChange={(nextValue) => setNextDueDate(nextValue ? new Date(nextValue) : undefined)}
        />

        <AppFieldGroup label="Notes (optional)">
          <AppTextArea
            value={notes}
            onChange={(event) => setNotes((event.target as HTMLTextAreaElement).value)}
            placeholder="Outcome, provider, products used, or anything else you want ready to hand"
            rows={3}
          />
        </AppFieldGroup>

        <AppButtonRow justifyContent="flex-end">
          <AppButton tone="outline" onPress={onClose}>
            Cancel
          </AppButton>
          <AppButton onPress={handleSave} disabled={!canSave || saving}>
            {saving ? 'Saving…' : 'Save pet care'}
          </AppButton>
        </AppButtonRow>
      </AppCardBody>
    </AppBottomSheet>
  );
}
